import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchFormsRequest } from '@client/model/policyApplicationModel';
import { selectCachedFormId, AppState } from '@client/model';

interface UseFormIdFromApiParams {
  stateCode?: string;
  formType: PolicyFormType;
  fundingPolicyTypeId?: number;
  enabled?: boolean;
}

/**
 * Hook to fetch form ID from the API based on state, form type, and funding policy type
 * Returns the formId from the API response
 * Uses Redux to cache the form ID and prevent duplicate API calls
 */
export const useFormIdFromApi = ({
  stateCode,
  formType,
  fundingPolicyTypeId,
  enabled = true,
}: UseFormIdFromApiParams): {
  formId: string | undefined;
  isLoading: boolean;
  error: string | null;
} => {
  const dispatch = useDispatch();

  // Generate cache key
  const cacheKey = useMemo(() => {
    if (!stateCode || !fundingPolicyTypeId) return '';
    return `${stateCode}-${formType}-${fundingPolicyTypeId}`;
  }, [stateCode, formType, fundingPolicyTypeId]);

  // Get cached form ID from Redux store
  const cachedFormId = useSelector((state: AppState) => 
    cacheKey ? selectCachedFormId(state, cacheKey) : undefined
  );

  useEffect(() => {
    // Don't fetch if disabled, missing required parameters, or already cached
    if (!enabled || !stateCode || !fundingPolicyTypeId || cachedFormId) {
      return;
    }

    // Dispatch Redux action to fetch form ID via API middleware
    // The middleware will automatically make a GET request to /api/forms with query parameters
    dispatch(
      fetchFormsRequest({
        queryParams: {
          stateCode,
          formType,
          fundingPolicyTypeId,
        },
        cacheKey, // Include cache key to store the result in Redux
      }),
    );
  }, [stateCode, formType, fundingPolicyTypeId, enabled, cachedFormId, dispatch, cacheKey]);

  return { 
    formId: cachedFormId, 
    isLoading: false, // Redux handles the loading state through middleware
    error: null, // Errors are handled silently with fallback to static form IDs
  };
};

